(() => {
  if (customElements.get('mc-product-description')) return;

  class McProductDescription extends HTMLElement {
    connectedCallback() {
      this.copy = this.querySelector('.mc-product-details__copy');
      if (!this.copy) return;
      this.observer?.disconnect();
      this.observer = new MutationObserver(() => this.structure());
      this.structure();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    hasContent(node) {
      return Boolean(node.textContent.replace(/\u00a0/g, ' ').trim() || node.querySelector?.('img, iframe, video, audio, input, table, svg'));
    }

    sectionKind(title) {
      const text = title.toLowerCase();
      if (/frequently asked|\bfaqs?\b/.test(text)) return 'faq';
      if (/care|caring|clean|maintenan|how to|using|usage|instruction|precaution|safety|safely|storing/.test(text)) return 'care';
      if (/specification|technical|product details|dimension|size chart|capacity guide|available.*(?:sizes|capacities)/.test(text)) return 'specs';
      if (/feature|benefit|why |included|includes|highlights|set contents/.test(text)) return 'features';
      return 'information';
    }

    normalizeSource() {
      this.copy.querySelectorAll('style').forEach(node => node.remove());
      this.copy.querySelectorAll('[style]').forEach(node => {
        if (node.closest('svg') || node.matches('img, iframe, video')) return;
        ['font', 'font-size', 'font-family', 'line-height', 'letter-spacing', 'color', 'background', 'background-color', 'text-align', 'margin', 'margin-top', 'margin-bottom', 'padding', 'width', 'height', 'min-width', 'max-width'].forEach(property => node.style.removeProperty(property));
        if (!node.getAttribute('style')?.trim()) node.removeAttribute('style');
      });
      this.copy.querySelectorAll('p, h1, h2, h3, h4, h5, h6').forEach(node => {
        if (!this.hasContent(node) && !node.id) node.remove();
      });
      this.copy.querySelectorAll('h1').forEach(node => {
        const heading = document.createElement('h2');
        if (node.id) heading.id = node.id;
        heading.append(...Array.from(node.childNodes));
        node.replaceWith(heading);
      });
      this.copy.querySelectorAll('ul > ul, ul > ol, ol > ul, ol > ol').forEach(node => {
        node.replaceWith(...Array.from(node.childNodes));
      });
    }

    flatten(nodes) {
      return Array.from(nodes).flatMap(node => {
        if (node.nodeType !== 1) return [node];
        const container = node.matches('div, section, article, center');
        const preserved = node.matches('.m-desc-feature-grid, .m-feature-card, .m-spec-card, .m-size-card, .m-spec-item, .m-size-row, .m-premium-video-wrapper') || node.querySelector('form, input, button, details');
        if (!container || preserved || !node.querySelector(':scope > p, :scope > div, :scope > section, :scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > ul, :scope > table')) return [node];
        const contents = this.flatten(node.childNodes);
        if (node.id) {
          const anchor = document.createElement('span');
          anchor.id = node.id;
          contents.unshift(anchor);
        }
        return contents;
      });
    }

    heading(node) {
      if (node.nodeType !== 1) return null;
      if (/^H[1-6]$/.test(node.tagName)) return node;
      if (node.tagName !== 'P' || node.textContent.trim().length > 110 || node.querySelector('a, img, iframe, video')) return null;
      const bold = node.querySelector(':scope > strong, :scope > b');
      if (!bold || bold.textContent.trim() !== node.textContent.trim()) return null;
      if (!/^(?:key features|product features|features|benefits|specifications|technical details|available sizes|size options|dimensions|care instructions|what.s included)\s*:?[\s\p{P}]*$/iu.test(node.textContent.trim())) return null;
      const heading = document.createElement('h3');
      if (node.id) heading.id = node.id;
      heading.append(...Array.from(node.childNodes));
      return heading;
    }

    makeGroup(kind, sourceHeading) {
      const heading = document.createElement('h3');
      heading.className = 'mc-description-section__heading';
      if (sourceHeading) {
        if (sourceHeading.id) heading.id = sourceHeading.id;
        heading.append(...Array.from(sourceHeading.childNodes));
      } else {
        heading.dataset.mcGeneratedHeading = '';
        heading.textContent = this.dataset[`${kind}Heading`] || this.dataset.overviewHeading;
      }
      if (!heading.textContent.trim()) {
        const label = document.createElement('span');
        label.dataset.mcGeneratedHeading = '';
        label.textContent = this.dataset[`${kind}Heading`] || this.dataset.overviewHeading;
        heading.append(label);
      }
      const body = document.createElement('div');
      body.className = 'mc-description-section__body';
      return { kind, heading, body, level: sourceHeading ? Number(sourceHeading.tagName.slice(1)) : 2 };
    }

    splitSpecification(item) {
      const text = item.textContent;
      const colon = text.indexOf(':');
      if (colon < 1 || text.slice(0, colon).trim().length > 40 || !text.slice(colon + 1).trim() || item.querySelector('ul, ol, table, iframe, video')) return;
      const walker = document.createTreeWalker(item, NodeFilter.SHOW_TEXT);
      let offset = colon + 1;
      let node;
      while ((node = walker.nextNode())) {
        if (offset <= node.textContent.length) break;
        offset -= node.textContent.length;
      }
      if (!node) return;
      const range = document.createRange();
      range.setStart(node, offset);
      range.setEnd(item, item.childNodes.length);
      const valueContent = range.extractContents();
      const label = document.createElement('div');
      const value = document.createElement('div');
      label.className = 'mc-detail-spec__label';
      value.className = 'mc-detail-spec__value';
      label.append(...Array.from(item.childNodes));
      value.append(valueContent);
      item.append(label, value);
      item.classList.add('mc-detail-spec');
    }

    decorate(group) {
      const contents = document.createDocumentFragment();
      let paragraph;
      Array.from(group.body.childNodes).forEach(node => {
        if (node.nodeType === 1 && node.matches('p, div, section, article, h1, h2, h3, h4, h5, h6, ul, ol, table, iframe, video, details, blockquote')) {
          paragraph = null;
          contents.append(node);
        } else {
          if (!paragraph) {
            if (!this.hasContent(node) && !node.id) return;
            paragraph = document.createElement('p');
            contents.append(paragraph);
          }
          paragraph.append(node);
        }
      });
      group.body.replaceChildren(contents);
      group.body.querySelectorAll('ul').forEach(list => {
        if (list.parentElement.closest('ul, ol')) return;
        if (group.kind === 'specs') {
          list.classList.add('mc-detail-specs');
          Array.from(list.children).forEach(item => this.splitSpecification(item));
        } else if (group.kind === 'features') {
          list.classList.add('mc-detail-cards');
        }
      });
      group.body.querySelectorAll('.m-spec-item').forEach(item => item.classList.add('mc-detail-spec'));
      group.body.querySelectorAll('.m-desc-feature-grid').forEach(grid => grid.classList.add('mc-detail-feature-grid'));
      group.body.querySelectorAll('table').forEach(table => {
        const wrapper = document.createElement('div');
        wrapper.className = 'mc-detail-table';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', group.heading.textContent.trim());
        table.before(wrapper);
        wrapper.append(table);
      });
      group.body.querySelectorAll('iframe, video').forEach(media => {
        media.classList.add('mc-detail-media');
        if (media.tagName === 'IFRAME') {
          media.setAttribute('loading', 'lazy');
          if (!media.title) media.title = this.dataset.videoHeading;
        }
      });
      const paragraphs = Array.from(group.body.children);
      if (group.kind === 'features' && paragraphs.length >= 2 && paragraphs.length <= 6 && paragraphs.every(node => node.tagName === 'P' && !node.querySelector('img, iframe, video')) && group.body.textContent.length < 1800) {
        const cards = document.createElement('div');
        cards.className = 'mc-detail-paragraph-cards';
        let card;
        let continuation = false;
        paragraphs.forEach(paragraph => {
          if (!continuation) {
            card = document.createElement('div');
            card.className = 'mc-detail-paragraph-card';
            cards.append(card);
          }
          card.append(paragraph);
          continuation = /:\s*$/.test(paragraph.textContent);
        });
        group.body.append(cards);
      }
    }

    disclosure(heading, body, className = '') {
      const details = document.createElement('details');
      details.className = `mc-description-accordion ${className}`.trim();
      const summary = document.createElement('summary');
      summary.append(heading);
      details.append(summary, body);
      return details;
    }

    shortenOverview(group) {
      let count = 0;
      let length = 0;
      const remainder = document.createElement('div');
      remainder.className = 'mc-description-section__body';
      Array.from(group.body.childNodes).forEach(node => {
        if (!this.hasContent(node)) return;
        const media = node.nodeType === 1 && (node.matches('iframe, video, img') || node.querySelector('iframe, video'));
        if (!media && (count >= 2 || length > 650)) remainder.append(node);
        else if (!media) {
          count += 1;
          length += node.textContent.length;
        }
      });
      if (this.hasContent(remainder)) {
        const heading = document.createElement('span');
        heading.dataset.mcGeneratedHeading = '';
        heading.textContent = this.dataset.readMoreHeading;
        group.body.append(this.disclosure(heading, remainder, 'mc-description-accordion--overview'));
      }
    }

    renderGroup(group, expanded) {
      if (!this.hasContent(group.body)) {
        const title = document.createElement('div');
        title.className = 'mc-description-title';
        title.append(group.heading);
        return title;
      }
      if (!expanded) return this.disclosure(group.heading, group.body, `mc-description-accordion--${group.kind}`);
      const section = document.createElement('section');
      section.className = `mc-description-section mc-description-section--${group.kind}`;
      section.append(group.heading, group.body);
      return section;
    }

    structure() {
      this.observer.disconnect();
      try {
        if (this.copy.querySelector(':scope > .mc-description-sections')) return;
        this.normalizeSource();
        const nodes = this.flatten(this.copy.childNodes);
        const groups = [];
        const scripts = [];
        let current;
        const start = (kind, heading) => {
          current = this.makeGroup(kind, heading);
          groups.push(current);
        };
        for (const node of nodes) {
          if (node.nodeType === 8) continue;
          if (node.nodeType === 3 && !node.textContent.trim()) {
            current?.body.append(node);
            continue;
          }
          if (node.nodeType === 1 && node.tagName === 'SCRIPT') {
            scripts.push(node);
            continue;
          }
          if (node.nodeType === 1 && node.tagName === 'HR') continue;
          const heading = this.heading(node);
          if (heading) {
            const level = Number(heading.tagName.slice(1));
            if (current && (current.kind === 'faq' && level > current.level)) {
              current.body.append(heading);
            } else if (current && !this.hasContent(current.body)) {
              current.body.append(heading);
            } else {
              let kind = this.sectionKind(heading.textContent);
              if (!groups.length && !/^(?:\W*)(?:product specifications|specifications|technical|key features|product features|care|what.s included|dimensions|size chart|frequently)/i.test(heading.textContent)) kind = 'overview';
              start(kind, heading);
            }
            continue;
          }
          if (node.nodeType === 1 && node.matches('.m-spec-card, .m-size-card')) {
            const title = node.querySelector(':scope > h2, :scope > h3, :scope > h4');
            start('specs', title);
            title?.remove();
            current.body.append(...Array.from(node.childNodes));
            continue;
          }
          if (node.nodeType === 1 && node.matches('.m-desc-feature-grid') && current?.kind !== 'features') start('features');
          if (node.nodeType === 1 && node.tagName === 'TABLE' && current?.kind !== 'specs') start('specs');
          if (node.nodeType === 1 && node.tagName === 'UL' && (!current || ['overview', 'information'].includes(current.kind))) start('features');
          if (!current) start('overview');
          current.body.append(node);
        }
        groups.forEach(group => this.decorate(group));
        const sections = document.createElement('div');
        sections.className = 'mc-description-sections';
        const overview = groups.find(group => group.kind === 'overview');
        const features = groups.filter(group => group.kind === 'features' && group.body.querySelector('.mc-detail-cards, .mc-detail-feature-grid, .mc-detail-paragraph-cards') && group.body.textContent.length < 3500);
        const feature = features.find(group => /key features|why |highlights/i.test(group.heading.textContent)) || features[0];
        const specs = groups.find(group => group.kind === 'specs' && group.body.querySelector('table, .mc-detail-specs, .mc-detail-spec, .m-size-row') && group.body.querySelectorAll('tr').length <= 14);
        const promoted = new Set([overview, feature, specs].filter(Boolean));
        if (overview) this.shortenOverview(overview);
        [overview, feature, specs].filter(Boolean).forEach(group => sections.append(this.renderGroup(group, true)));
        const remaining = groups.filter(group => !promoted.has(group));
        if (remaining.length) {
          const more = document.createElement('section');
          more.className = 'mc-description-more';
          const title = document.createElement('h3');
          title.className = 'mc-description-section__heading';
          title.dataset.mcGeneratedHeading = '';
          title.textContent = this.dataset.moreHeading;
          more.append(title, ...remaining.map(group => this.renderGroup(group, false)));
          sections.append(more);
        }
        this.copy.replaceChildren(sections, ...scripts);
      } finally {
        this.observer.observe(this.copy, { childList: true, subtree: true });
      }
    }
  }

  customElements.define('mc-product-description', McProductDescription);
})();
